import { haversineKm } from './plan-partitioning';
import { LatLon } from './route-optimization.interface';

/**
 * Decodifica la `geometry` de `POST /sort` — una polyline codificada— a puntos
 * dibujables.
 *
 * Existe porque las dos fuentes de geometría de esta pantalla no vienen igual: el
 * `geojson` de `/route-map` trae la línea **ya decodificada por el servidor**
 * (*"polyline ya decodificado en el servidor"*, ver `RouteOptimizationService`),
 * y `/sort` devuelve la cadena cruda. En el repo no había decodificador ni
 * dependencia que lo trajera.
 *
 * **La precisión no está en el contrato**, y es el único riesgo real: Google usa 5
 * decimales y Valhalla 6, la misma cadena decodifica con las dos **sin error**, y
 * la diferencia es un factor de diez — un trayecto a cientos de kilómetros de
 * donde va. Por eso `decodePolylineNear` la decide con un dato que ya tenemos (el
 * almacén de la ruta) en vez de suponerla.
 *
 * Módulo puro: entra una cadena, salen pares `[lat, lon]`. Sin HTTP, sin Angular.
 */

/** Un punto dibujable, en el mismo orden que usa Leaflet y `RoutePolyline`. */
export type PolylinePoint = [number, number];

/** Las dos precisiones posibles, en el orden en que se prueban. */
const PRECISIONS = [6, 5] as const;

/**
 * A cuántos km del almacén puede empezar un trayecto para considerarlo plausible.
 *
 * Generoso a propósito: una ruta puede arrancar lejos si el almacén no está en el
 * territorio. No hace falta ajustar fino porque el error que se busca separar es de
 * **orden de magnitud** — la precisión equivocada aterriza a miles de km o fuera
 * del rango de una coordenada.
 */
const PLAUSIBLE_START_KM = 300;

/** ¿Es un par que puede existir en el planeta? */
function isRealCoordinate(lat: number, lon: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  );
}

/**
 * El algoritmo estándar de polylines codificadas, con la precisión dada.
 *
 * Devuelve vacío ante una cadena ausente o que produzca coordenadas imposibles: es
 * el caso de decodificar con la precisión equivocada, y emitir esos puntos dibujaría
 * una línea a la nada en vez de no dibujar nada.
 */
export function decodePolyline(encoded: string, precision: number): PolylinePoint[] {
  if (!encoded) return [];
  const factor = Math.pow(10, precision);
  const points: PolylinePoint[] = [];
  let index = 0;
  let lat = 0;
  let lon = 0;

  while (index < encoded.length) {
    for (const axis of [0, 1]) {
      let result = 0;
      let shift = 0;
      let byte: number;
      do {
        byte = encoded.charCodeAt(index++) - 63;
        if (!Number.isFinite(byte) || byte < 0) return [];
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20 && index <= encoded.length);
      const delta = result & 1 ? ~(result >> 1) : result >> 1;
      if (axis === 0) lat += delta;
      else lon += delta;
    }
    const point: PolylinePoint = [lat / factor, lon / factor];
    if (!isRealCoordinate(point[0], point[1])) return [];
    points.push(point);
  }

  return points;
}

/**
 * Decodifica eligiendo la precisión **por el dato**: gana la que hace que el
 * trayecto empiece cerca de `reference` (el almacén de la ruta, que ya conocemos).
 *
 * Si ninguna cae cerca no se devuelve nada. Es el mismo default honesto que la
 * pantalla ya aplica a una ruta editada: antes no dibujar que afirmar un recorrido
 * que nadie va a hacer.
 */
export function decodePolylineNear(
  encoded: string,
  reference: LatLon | null | undefined,
): PolylinePoint[] {
  if (!encoded || !reference) return [];
  const from: PolylinePoint = [reference.lat, reference.lon];
  if (!isRealCoordinate(from[0], from[1])) return [];

  let best: { points: PolylinePoint[]; km: number } | undefined;
  for (const precision of PRECISIONS) {
    const points = decodePolyline(encoded, precision);
    if (!points.length) continue;
    const km = haversineKm(from, points[0]);
    if (km > PLAUSIBLE_START_KM) continue;
    if (!best || km < best.km) best = { points, km };
  }
  return best?.points ?? [];
}
